/* import __COLOCATED_TEMPLATE__ from './sessions-grid.hbs.js'; */
import { buildTask as _buildTask } from "ember-concurrency/async-arrow-runtime";
var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { isEmpty } from '@ember/utils';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { next } from '@ember/runloop';
import { dropTask } from 'ember-concurrency';
import { DateTime } from 'luxon';
import { filter, map } from 'rsvp';
import escapeRegExp from 'ilios-common/utils/escape-reg-exp';
import { sortBy } from 'ilios-common/utils/array-helpers';
import { TrackedAsyncData } from 'ember-async-data';
let SessionsGrid = (_class = class SessionsGrid extends Component {
  constructor() {
    super(...arguments);
    _initializerDefineProperty(this, "router", _descriptor, this);
    _initializerDefineProperty(this, "preserveScroll", _descriptor2, this);
    _initializerDefineProperty(this, "intl", _descriptor3, this);
    _initializerDefineProperty(this, "confirmDeleteSessionIds", _descriptor4, this);
    _defineProperty(this, "removeSession", _buildTask(() => ({
      context: this,
      generator: function* (session) {
        session.deleteRecord();
        yield session.save();
      }
    }), null, "removeSession", "drop"));
    this.scrollDown();
  }
  get sortedSessionsData() {
    return new TrackedAsyncData(this.sortSessions(this.args.sessions, this.args.filterBy, this.sortInfo));
  }
  get sortedSessions() {
    return this.sortedSessionsData.isResolved ? this.sortedSessionsData.value : [];
  }
  async sortSessions(sessions, filterBy, sortInfo) {
    const filteredSessions = await this.filterSessions(sessions, filterBy);
    switch (sortInfo.column) {
      case 'sessionTypeTitle':
        return this.sortBySessionTypeTitle(filteredSessions, sortInfo);
      case 'learnerGroupCount':
        return this.sortByLearnerGroupCount(filteredSessions, sortInfo);
      case 'firstOfferingDate':
        return this.sortByFirstOfferingDate(filteredSessions, sortInfo);
      case 'status':
        return this.sortByStatus(filteredSessions, sortInfo);
      default:
        if (sortInfo.descending) {
          return sortBy(filteredSessions, sortInfo.column).reverse();
        }
        return sortBy(filteredSessions, sortInfo.column);
    }
  }
  sessionStatus(session) {
    let status = this.intl.t('general.notPublished');
    if (session.published) {
      status = this.intl.t('general.published');
    }
    if (session.publishedAsTbd) {
      status = this.intl.t('general.scheduled');
    }
    return status.toString();
  }
  sortByStatus(sessions, sortInfo) {
    const sortProxies = sessions.map(session => {
      return {
        session,
        status: this.sessionStatus(session)
      };
    });
    const sortedSessions = sortBy(sortProxies, 'status').map(proxy => proxy.session);
    return sortInfo.descending ? sortedSessions.reverse() : sortedSessions;
  }
  async sortBySessionTypeTitle(sessions, sortInfo) {
    const sortProxies = await map(sessions, async session => {
      const sessionType = await session.sessionType;
      const sessionTypeTitle = sessionType?.title;
      return {
        session,
        title: sessionTypeTitle
      };
    });
    const sortedSessions = sortBy(sortProxies, 'title').map(proxy => proxy.session);
    return sortInfo.descending ? sortedSessions.reverse() : sortedSessions;
  }
  async sortByLearnerGroupCount(sessions, sortInfo) {
    const sortProxies = await map(sessions, async session => {
      const offerings = await session.offerings;
      const learnerGroups = await map(offerings, async offering => {
        return await offering.learnerGroups;
      });
      return {
        session,
        learnerGroupCount: learnerGroups.flat().length
      };
    });
    const sortedSessions = sortBy(sortProxies, 'learnerGroupCount').map(proxy => proxy.session);
    return sortInfo.descending ? sortedSessions.reverse() : sortedSessions;
  }
  async sortByFirstOfferingDate(sessions, sortInfo) {
    const sortProxies = await map(sessions, async session => {
      const firstOfferingDate = await this.getFirstOfferingDate(session);
      return {
        session,
        firstOfferingDate
      };
    });
    const sortedSessions = sortBy(sortProxies, 'firstOfferingDate').map(proxy => proxy.session);
    return sortInfo.descending ? sortedSessions.reverse() : sortedSessions;
  }
  async getFirstOfferingDate(session) {
    if (session.isIndependentLearning) {
      return (await session.ilmSession).dueDate;
    }
    const offerings = await session.offerings;
    return offerings.filter(offering => Boolean(offering.startDate)).sort((a, b) => {
      const aDate = DateTime.fromJSDate(a.startDate);
      const bDate = DateTime.fromJSDate(b.startDate);
      if (aDate === bDate) {
        return 0;
      }
      return aDate > bDate ? 1 : -1;
    })[0]?.startDate;
  }
  async filterSessions(sessions, filterBy) {
    if (isEmpty(sessions)) {
      return [];
    }
    if (isEmpty(filterBy)) {
      return sessions;
    }
    const filterExpressions = filterBy.split(' ').map(function (string) {
      const clean = escapeRegExp(string);
      return new RegExp(clean, 'gi');
    });
    return filter(sessions, async session => {
      let matchedSearchTerms = 0;
      const sessionType = await session.sessionType;
      const sessionTypeTitle = sessionType?.title;
      const searchString = session.title + sessionTypeTitle + this.sessionStatus(session);
      for (let i = 0; i < filterExpressions.length; i++) {
        if (searchString.match(filterExpressions[i])) {
          matchedSearchTerms++;
        }
      }
      //if the number of matching search terms is equal to the number searched, return true
      return matchedSearchTerms === filterExpressions.length;
    });
  }
  get sortInfo() {
    const parts = this.args.sortBy.split(':');
    const column = parts[0];
    const descending = parts.length > 1 && parts[1] === 'desc';
    return {
      column,
      descending,
      sortBy: this.args.sortBy
    };
  }
  scrollDown() {
    const position = this.preserveScroll.getPosition('session-list');
    // eslint-disable-next-line ember/no-runloop
    next(() => {
      if (position) {
        window.scroll(0, position);
      }
    });
  }
  confirmDelete(sessionId) {
    this.confirmDeleteSessionIds = [...this.confirmDeleteSessionIds, sessionId];
  }
  cancelDelete(sessionId) {
    this.confirmDeleteSessionIds = this.confirmDeleteSessionIds.filter(id => id !== sessionId);
  }
  expandSession(session) {
    if (session.offeringCount) {
      this.args.expandSession(session);
    }
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "router", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "preserveScroll", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "intl", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "confirmDeleteSessionIds", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _applyDecoratedDescriptor(_class.prototype, "sortedSessionsData", [cached], Object.getOwnPropertyDescriptor(_class.prototype, "sortedSessionsData"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "scrollDown", [action], Object.getOwnPropertyDescriptor(_class.prototype, "scrollDown"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "confirmDelete", [action], Object.getOwnPropertyDescriptor(_class.prototype, "confirmDelete"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cancelDelete", [action], Object.getOwnPropertyDescriptor(_class.prototype, "cancelDelete"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "expandSession", [action], Object.getOwnPropertyDescriptor(_class.prototype, "expandSession"), _class.prototype), _class);
export { SessionsGrid as default };